import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import Text from "@/components/Text";
import Theme from "@/utils/theme";

type LoaderContext = {
  show: (val: string, message?: string) => void;
  hide: (val: string) => void;
};

const LoaderContext = createContext<LoaderContext>({} as LoaderContext);

type LoaderProviderProps = {
  children: React.ReactNode;
};

export const LoaderProvider = ({ children }: LoaderProviderProps) => {
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  const [messagesById, setMessagesById] = useState<Record<string, string>>({});

  const isLoading = useMemo(() => {
    return loadingIds.length > 0;
  }, [loadingIds]);

  const activeMessage = useMemo(() => {
    for (let i = loadingIds.length - 1; i >= 0; i--) {
      const msg = messagesById[loadingIds[i]];
      if (msg) return msg;
    }
    return undefined;
  }, [loadingIds, messagesById]);

  const show = useCallback((val: string, message?: string) => {
    setLoadingIds((current) => [...current, val]);
    if (message) {
      setMessagesById((current) => ({ ...current, [val]: message }));
    }
  }, []);

  const hide = useCallback((val: string) => {
    setLoadingIds((current) => current.filter((id) => id !== val));
    setMessagesById((current) => {
      const next = { ...current };
      delete next[val];
      return next;
    });
  }, []);

  const contextValue = useMemo(
    () => ({ show, hide }),
    [show, hide]
  );

  return (
    <LoaderContext.Provider value={contextValue}>
      <Backdrop
        sx={{
          color: Theme.colors.brand.primary,
          /** Below MUI Modal/Dialog (zIndex.modal) so alerts & session modals stay clickable */
          zIndex: (theme) => theme.zIndex.modal - 1,
        }}
        open={isLoading}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          px={2}
        >
          <CircularProgress color="inherit" />
          {activeMessage ? (
            <Text
              textAlign="center"
              color="#fff"
              fontSize="1rem"
              maxWidth="20rem"
            >
              {activeMessage}
            </Text>
          ) : null}
        </Box>
      </Backdrop>

      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = (): LoaderContext => useContext(LoaderContext);
